import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Chip,
  Stack,
  Divider,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  useMediaQuery,
  InputAdornment
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Add as AddIcon,
  AutoAwesome as AIIcon,
  School as SchoolIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { setsApi, partsApi, providerApi, mediaApi, setPartsApi, setToolsApi } from '../services/api';
import { fetchSampleSetPhotoFile } from '../utils/setPhoto';
import { getSetPhotoUploadGuidance } from '../utils/compressImageForUpload';
import AILearningOutcomeGenerator from './AILearningOutcomeGenerator';
import AISetDescriptionHelper from './AISetDescriptionHelper';
import {
  resolveProviderMarkupPercentage,
  resolveSystemCommissionPercentage,
} from '../utils/providerMarkup';
import { normalizeLearningOutcomes } from '../utils/learningOutcomes';
import WizardPartsToolsStep, {
  WizardPartSelection,
  WizardToolSelection,
} from './WizardPartsToolsStep';

interface ProviderSetFormData {
  name: string;
  description: string;
  category: string;
  difficulty_level: string;
  recommended_age_min: number;
  recommended_age_max: number;
  estimated_duration_minutes: number;
  base_price: number;
  available_quantity: number;
  video_url: string;
  learning_outcomes: string[];
  manual: string;
  active: boolean;
  photos: File[];
  parts: WizardPartSelection[];
  tools: WizardToolSelection[];
}

interface ProviderSetCreationWizardProps {
  open: boolean;
  onClose: () => void;
  onSetCreated: () => void;
  editingSet?: any;
}

const ProviderSetCreationWizard: React.FC<ProviderSetCreationWizardProps> = ({
  open,
  onClose,
  onSetCreated,
  editingSet
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [aiGeneratorOpen, setAiGeneratorOpen] = useState(false);

  // Initialize form data when editing
  useEffect(() => {
    if (editingSet) {
      setFormData({
        name: editingSet.name || '',
        description: editingSet.description || '',
        category: editingSet.category || '',
        difficulty_level: editingSet.difficulty_level || '',
        recommended_age_min: editingSet.recommended_age_min || 6,
        recommended_age_max: editingSet.recommended_age_max || 100,
        estimated_duration_minutes: editingSet.estimated_duration_minutes || 120,
        base_price: editingSet.price || editingSet.base_price || 0,
        available_quantity: editingSet.available_quantity || 1,
        video_url: editingSet.video_url || '',
        learning_outcomes: normalizeLearningOutcomes(editingSet.learning_outcomes),
        manual: editingSet.manual || '',
        active: editingSet.is_active !== undefined ? editingSet.is_active : true,
        photos: [],
        parts: (editingSet.parts || []).map((p: any) => ({
          part_id: p.part_id,
          part_name: p.part_name || p.name,
          part_number: p.part_number,
          quantity: p.quantity ?? 1,
          is_optional: Boolean(p.is_optional),
          notes: p.notes || '',
        })),
        tools: (editingSet.tools || []).map((t: any) => ({
          tool_id: t.tool_id,
          tool_name: t.tool_name || t.name,
          tool_number: t.tool_number,
          quantity: t.quantity ?? 1,
          is_optional: Boolean(t.is_optional),
          notes: t.notes || '',
        })),
      });
    }
  }, [editingSet]);

  // Provider list rows usually omit parts[] — load linked parts/tools for edit.
  useEffect(() => {
    if (!open || !editingSet?.set_id) return;
    let cancelled = false;
    (async () => {
      try {
        const [partsRes, toolsRes] = await Promise.all([
          setPartsApi.getBySetId(editingSet.set_id),
          setToolsApi.getBySetId(editingSet.set_id).catch(() => ({ data: { tools: [] } })),
        ]);
        if (cancelled) return;
        const partsRaw =
          (partsRes as any)?.data?.parts ||
          (Array.isArray((partsRes as any)?.data) ? (partsRes as any).data : []) ||
          [];
        const toolsRaw =
          (toolsRes as any)?.data?.tools ||
          (Array.isArray((toolsRes as any)?.data) ? (toolsRes as any).data : []) ||
          [];
        setFormData((prev) => ({
          ...prev,
          parts: partsRaw
            .filter((p: any) => p?.part_id != null)
            .map((p: any) => ({
              part_id: Number(p.part_id),
              part_name: p.part_name || p.name,
              part_number: p.part_number,
              quantity: Number(p.quantity ?? 1) || 1,
              is_optional: Boolean(p.is_optional),
              notes: p.notes || '',
            })),
          tools: toolsRaw
            .filter((t: any) => t?.tool_id != null)
            .map((t: any) => ({
              tool_id: Number(t.tool_id),
              tool_name: t.tool_name || t.name,
              tool_number: t.tool_number,
              quantity: Number(t.quantity ?? 1) || 1,
              is_optional: Boolean(t.is_optional),
              notes: t.notes || '',
            })),
        }));
      } catch (err) {
        console.warn('Failed to load existing parts/tools for provider edit:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, editingSet?.set_id]);

  const [formData, setFormData] = useState<ProviderSetFormData>({
    name: '',
    description: '',
    category: '',
    difficulty_level: '',
    recommended_age_min: 6,
    recommended_age_max: 100,
    estimated_duration_minutes: 120,
    base_price: 15,
    available_quantity: 1,
    video_url: '',
    learning_outcomes: [],
    manual: '',
    active: true,
    photos: [],
    parts: [],
    tools: [],
  });

  // Always treat learning outcomes as string[] — API/DB may return JSON text.
  const learningOutcomes = normalizeLearningOutcomes(formData.learning_outcomes);

  const [availableCategories] = useState([
    'electronics',
    'woodworking',
    'mechanical',
    'programming',
    'robotics',
    'chemistry',
    'physics',
    'biology',
    'art',
    'crafts'
  ]);

  const [availableDifficulties] = useState([
    'beginner',
    'intermediate',
    'advanced',
    'expert'
  ]);

  const steps = [
    'Basic Information',
    'Parts & Tools',
    'Content & Instructions',
    'Pricing & Media',
    'Review & Create',
  ];

  const aiMaterials = [
    ...formData.parts.map((p) => ({
      name: p.part_name || p.part_number || `Part ${p.part_id}`,
      quantity: p.quantity,
      kind: 'part' as const,
    })),
    ...formData.tools.map((t) => ({
      name: t.tool_name || t.tool_number || `Tool ${t.tool_id}`,
      quantity: t.quantity,
      kind: 'tool' as const,
    })),
  ];

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Basic Information
        if (!formData.name.trim()) {
          setError('Set name is required');
          return false;
        }
        if (!formData.category) {
          setError('Category is required');
          return false;
        }
        if (!formData.difficulty_level) {
          setError('Difficulty level is required');
          return false;
        }
        break;
      case 1: // Parts & Tools
        if (formData.parts.length === 0) {
          setError('Add at least one part before generating instructions');
          return false;
        }
        break;
      case 2: // Content & Instructions
        if (!formData.description.trim()) {
          setError('Description is required');
          return false;
        }
        break;
      case 3: // Pricing & Media
        if (formData.base_price <= 0) {
          setError('Base price must be greater than 0');
          return false;
        }
        if (formData.available_quantity < 0) {
          setError('Available quantity must be 0 or greater');
          return false;
        }
        break;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;

    try {
      setLoading(true);
      setError(null);

      if (editingSet) {
        // Editing existing provider set
        if (editingSet.provider_set_id) {
          // Update provider set specific fields
          await providerApi.updateProviderSet(editingSet.provider_set_id, {
            price: formData.base_price,
            available_quantity: formData.available_quantity,
            is_active: formData.active
          });
        }
        
        // Upload photos if any
        if (formData.photos.length > 0 && editingSet.set_id) {
          console.log('📸 Uploading photos for set:', editingSet.set_id);
          for (const photo of formData.photos) {
            try {
              await mediaApi.upload(
                photo, 
                editingSet.set_id, 
                undefined, 
                'set_photo', 
                `Photo for ${formData.name}`, 
                `Photo showing ${formData.name}`
              );
            } catch (photoError) {
              console.warn('Failed to upload photo:', photoError);
              // Continue with other photos even if one fails
            }
          }
        }
        
        setSuccess('Set updated successfully!');
        setTimeout(() => {
          onSetCreated();
          handleClose();
        }, 2000);
      } else {
        // Creating new set
        const systemCommissionPercentage = resolveSystemCommissionPercentage(user);

        // Create the set (omit hard-coded commission part_id — server seeds SYS-COMM;
        // missing part ids must not block set creation)
        type SetPartInput = {
          part_id: number;
          quantity: number;
          is_optional: boolean;
          notes: string;
          safety_notes: string;
        };
        const setParts: SetPartInput[] = formData.parts.map((p) => ({
          part_id: p.part_id,
          quantity: p.quantity ?? 1,
          is_optional: Boolean(p.is_optional),
          notes: p.notes || '',
          safety_notes: '',
        }));

        // Attach commission part when it exists in the catalog
        try {
          const partsResp = await partsApi.getAll();
          const commissionPart = (partsResp.data?.parts || []).find(
            (p: any) => p.part_number === 'SYS-COMM' || p.part_id === 60
          );
          if (
            commissionPart?.part_id &&
            !setParts.some((p) => p.part_id === commissionPart.part_id)
          ) {
            setParts.push({
              part_id: commissionPart.part_id,
              quantity: 1,
              is_optional: false,
              notes: `System commission (${systemCommissionPercentage}%)`,
              safety_notes: 'Platform service fee'
            });
          }
        } catch (partsLookupErr) {
          console.warn('Commission part lookup skipped:', partsLookupErr);
        }

        const setData = {
          name: formData.name,
          description: formData.description,
          category: formData.category,
          difficulty_level: formData.difficulty_level,
          recommended_age_min: formData.recommended_age_min,
          recommended_age_max: formData.recommended_age_max,
          estimated_duration_minutes: formData.estimated_duration_minutes,
          base_price: formData.base_price,
          video_url: formData.video_url,
          learning_outcomes: learningOutcomes,
          manual: formData.manual,
          active: formData.active,
          translations: [
            {
              language_code: 'en',
              name: formData.name,
              description: formData.description
            }
          ],
          parts: setParts,
          tools: formData.tools.map((t) => ({
            tool_id: t.tool_id,
            quantity: t.quantity ?? 1,
            is_optional: Boolean(t.is_optional),
            notes: t.notes || '',
          })),
        };

        // First create the set
        const setResponse = await setsApi.create(setData);
        const createdSetId = setResponse.data.set_id;

        const providerId = user?.user_id ?? (user as any)?.id;
        if (!providerId) {
          throw new Error('Logged-in user id is missing; cannot link provider set');
        }

        // Link set to this provider (send provider_id for admin sessions too)
        await providerApi.createProviderSet({
          set_id: createdSetId,
          provider_id: providerId,
          price: formData.base_price,
          available_quantity: formData.available_quantity,
          is_active: true
        });
        
        // Upload photos if any; otherwise attach the bundled sample photo
        if (formData.photos.length > 0) {
          console.log('📸 Uploading photos for new set:', createdSetId);
          for (const photo of formData.photos) {
            try {
              await mediaApi.upload(
                photo, 
                createdSetId, 
                undefined, 
                'set_photo', 
                `Photo for ${formData.name}`, 
                `Photo showing ${formData.name}`
              );
            } catch (photoError) {
              console.warn('Failed to upload photo:', photoError);
              // Continue with other photos even if one fails
            }
          }
        } else {
          try {
            const sample = await fetchSampleSetPhotoFile(formData.category);
            await mediaApi.upload(
              sample,
              createdSetId,
              undefined,
              'set_photo',
              `Sample photo for ${formData.name}`,
              'Sample set photo'
            );
          } catch (sampleErr) {
            console.warn('Sample photo attach skipped:', sampleErr);
          }
        }
        
        setSuccess('Set created successfully!');
        setTimeout(() => {
          onSetCreated();
          handleClose();
        }, 2000);
      }

    } catch (err: any) {
      console.error('Error saving set:', err);
      const apiError = err.response?.data?.error || err.response?.data?.details;
      setError(apiError ? `Failed to create set: ${apiError}` : 'Failed to create set');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      difficulty_level: '',
      recommended_age_min: 6,
      recommended_age_max: 100,
      estimated_duration_minutes: 120,
      base_price: 15,
      available_quantity: 1,
      video_url: '',
      learning_outcomes: [],
      manual: '',
      active: true,
      photos: [],
      parts: [],
      tools: [],
    });
    setActiveStep(0);
    setError(null);
    setSuccess(null);
    onClose();
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0: // Basic Information
        return (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Set Name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter set name"
                  required
                />
              </Grid>
              
              <Grid size={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    label="Category"
                  >
                    {availableCategories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={6}>
                <FormControl fullWidth required>
                  <InputLabel>Difficulty Level</InputLabel>
                  <Select
                    value={formData.difficulty_level}
                    onChange={(e) => setFormData(prev => ({ ...prev, difficulty_level: e.target.value }))}
                    label="Difficulty Level"
                  >
                    {availableDifficulties.map((difficulty) => (
                      <MenuItem key={difficulty} value={difficulty}>
                        {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={4}>
                <TextField
                  fullWidth
                  label="Min Age"
                  type="number"
                  value={formData.recommended_age_min}
                  onChange={(e) => setFormData(prev => ({ ...prev, recommended_age_min: parseInt(e.target.value) || 6 }))}
                  inputProps={{ min: 1, max: 100 }}
                />
              </Grid>
              
              <Grid size={4}>
                <TextField
                  fullWidth
                  label="Max Age"
                  type="number"
                  value={formData.recommended_age_max}
                  onChange={(e) => setFormData(prev => ({ ...prev, recommended_age_max: parseInt(e.target.value) || 100 }))}
                  inputProps={{ min: 1, max: 100 }}
                />
              </Grid>
              
              <Grid size={4}>
                <TextField
                  fullWidth
                  label="Duration (minutes)"
                  type="number"
                  value={formData.estimated_duration_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, estimated_duration_minutes: parseInt(e.target.value) || 120 }))}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1: // Parts & Tools
        return (
          <WizardPartsToolsStep
            parts={formData.parts}
            tools={formData.tools}
            seedSysComm={!editingSet}
            onPartsChange={(parts) => setFormData((prev) => ({ ...prev, parts }))}
            onToolsChange={(tools) => setFormData((prev) => ({ ...prev, tools }))}
          />
        );

      case 2: // Content & Instructions
        return (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Using {formData.parts.length} part(s) and {formData.tools.length} tool(s) for instructions
              and learning outcomes.
            </Alert>
            <Grid container spacing={3}>
              <Grid size={12}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="subtitle2">Description</Typography>
                  <AISetDescriptionHelper
                    name={formData.name}
                    category={formData.category}
                    difficultyLevel={formData.difficulty_level}
                    ageMin={formData.recommended_age_min}
                    ageMax={formData.recommended_age_max}
                    durationMinutes={formData.estimated_duration_minutes}
                    materials={aiMaterials}
                    currentDescription={formData.description}
                    onGenerated={(description) =>
                      setFormData((prev) => ({ ...prev, description }))
                    }
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what students will learn and build with the selected parts and tools"
                  required
                />
              </Grid>
              
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Instructions & Manual"
                  multiline
                  rows={6}
                  value={formData.manual}
                  onChange={(e) => setFormData(prev => ({ ...prev, manual: e.target.value }))}
                  placeholder="Step-by-step instructions that reference the parts and tools you selected"
                />
              </Grid>
              
              {/* Learning Outcomes Section */}
              <Grid size={12}>
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SchoolIcon color="primary" />
                      Learning Outcomes
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AIIcon />}
                      onClick={() => setAiGeneratorOpen(true)}
                      disabled={!formData.category || !formData.difficulty_level || formData.parts.length === 0}
                    >
                      AI Generator
                    </Button>
                  </Box>
                  
                  {learningOutcomes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                      No learning outcomes yet. Use the AI Generator — it uses the parts and tools you
                      selected in the previous step.
                    </Typography>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {learningOutcomes.length} learning outcome{learningOutcomes.length !== 1 ? 's' : ''} defined:
                      </Typography>
                      <Stack spacing={1}>
                        {learningOutcomes.map((outcome, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Typography variant="body2" sx={{ flex: 1, py: 0.5 }}>
                              {index + 1}. {outcome}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const updatedOutcomes = learningOutcomes.filter((_, i) => i !== index);
                                setFormData(prev => ({ ...prev, learning_outcomes: updatedOutcomes }));
                              }}
                              color="error"
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Learning Outcomes:</strong> Tailored to the parts and tools in this set
                      so educators see what students practice with the real materials.
                    </Typography>
                  </Alert>
                </Box>
              </Grid>
            </Grid>
          </Box>
        );

      case 3: // Pricing & Media
        return (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Base Price"
                  type="number"
                  value={formData.base_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                  inputProps={{ step: "0.01", min: "0.01" }}
                  required
                  error={formData.base_price <= 0}
                  helperText={formData.base_price <= 0 ? 'Price is required' : 'Default €15 — edit if needed'}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                />
              </Grid>
              
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Available Quantity"
                  type="number"
                  value={formData.available_quantity}
                  onChange={(e) => setFormData(prev => ({ ...prev, available_quantity: parseInt(e.target.value) || 0 }))}
                  inputProps={{ min: "0" }}
                  required
                />
              </Grid>
              
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Video URL (optional)"
                  value={formData.video_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </Grid>
              
              {/* Photo Upload Section */}
              <Grid size={12}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PhotoCameraIcon color="primary" />
                  Set Photos
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="photo-upload"
                    multiple
                    type="file"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setFormData(prev => ({ ...prev, photos: [...prev.photos, ...files] }));
                    }}
                  />
                  <label htmlFor="photo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCameraIcon />}
                      sx={{ mb: 2 }}
                    >
                      Add Photos
                    </Button>
                  </label>
                </Box>
                
                {/* Display uploaded photos */}
                {formData.photos.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {formData.photos.map((photo, index) => (
                      <Box key={index} sx={{ position: 'relative' }}>
                        <img
                          src={URL.createObjectURL(photo)}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: 100,
                            height: 100,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #ddd'
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            bgcolor: 'error.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'error.dark' }
                          }}
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              photos: prev.photos.filter((_, i) => i !== index)
                            }));
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
                
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">{getSetPhotoUploadGuidance()}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Tip: 1600×1200 or square 1600×1600, JPG/PNG, under ~2MB. Larger files shrink automatically.
                  </Typography>
                </Alert>
              </Grid>
              
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                    />
                  }
                  label="Active (visible in shop)"
                />
              </Grid>
              
              {/* System Commission Info */}
              <Grid size={12}>
                <Card sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <SecurityIcon color="primary" />
                      System Commission
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      A system commission part will be automatically added to your set to represent the platform's service fee.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        label={`Your markup: ${resolveProviderMarkupPercentage(user)}%`}
                        color="success"
                        size="small"
                      />
                      <Chip 
                        label={`System commission: ${resolveSystemCommissionPercentage(user)}%`}
                        color="warning"
                        size="small"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 4: // Review & Create
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Review Your Set
            </Typography>
            
            <Grid container spacing={2}>
              <Grid size={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>{formData.name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {formData.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip label={formData.category} size="small" />
                      <Chip label={formData.difficulty_level} size="small" />
                      <Chip label={`${formData.recommended_age_min}-${formData.recommended_age_max} years`} size="small" />
                      <Chip label={`${formData.estimated_duration_minutes} min`} size="small" />
                      <Chip label={`${formData.parts.length} parts`} size="small" color="primary" />
                      <Chip label={`${formData.tools.length} tools`} size="small" color="secondary" />
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" color="primary">
                        €{formData.base_price.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Available: {formData.available_quantity} units
                      </Typography>
                    </Box>
                    
                    {learningOutcomes.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>Learning Outcomes:</Typography>
                        <Stack spacing={0.5}>
                          {learningOutcomes.map((outcome, index) => (
                            <Typography key={index} variant="body2" sx={{ pl: 1 }}>
                              • {outcome}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{editingSet ? 'Edit Set' : 'Create New Set'}</Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Stepper
            activeStep={activeStep}
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ mb: 3 }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {renderStepContent(activeStep)}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          
          {activeStep > 0 && (
            <Button onClick={handleBack}>
              Back
            </Button>
          )}
          
          {activeStep < steps.length - 1 ? (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button 
              variant="contained" 
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {loading ? 'Creating...' : 'Create Set'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* AI Learning Outcome Generator Dialog */}
      <AILearningOutcomeGenerator
        open={aiGeneratorOpen}
        onClose={() => setAiGeneratorOpen(false)}
        onApply={(outcomes: string[]) => {
          setFormData(prev => ({ ...prev, learning_outcomes: normalizeLearningOutcomes(outcomes) }));
          setAiGeneratorOpen(false);
        }}
        currentOutcomes={learningOutcomes}
        setCategory={formData.category}
        setDifficulty={formData.difficulty_level}
        setAgeRange={{ min: formData.recommended_age_min, max: formData.recommended_age_max }}
        setDuration={formData.estimated_duration_minutes}
        setMaterials={aiMaterials}
      />
    </>
  );
};

export default ProviderSetCreationWizard;
